import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'theme-preference';

const getSystemPref = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
};

const getInitialTheme = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch (_e) {
    /* ignore */
  }
  return getSystemPref();
};

const applyTheme = (theme) => {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
  root.style.colorScheme = theme;
};

/**
 * useTheme Hook – verwaltet Light/Dark Mode mit System-Sync und Persistierung.
 * Wenn der Nutzer aktiv eine Auswahl trifft, wird sie gespeichert.
 * Ohne aktive Auswahl folgt das Theme dem Systemzustand live.
 */
export const useTheme = () => {
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    return getInitialTheme();
  });

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // System-Änderungen live übernehmen, wenn keine User-Auswahl gespeichert ist
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handle = (e) => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored !== 'dark' && stored !== 'light') {
          setTheme(e.matches ? 'dark' : 'light');
        }
      } catch (_e) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };
    if (mql.addEventListener) {
      mql.addEventListener('change', handle);
      return () => mql.removeEventListener('change', handle);
    }
    mql.addListener(handle);
    return () => mql.removeListener(handle);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch (_e) {
        /* ignore */
      }
      return next;
    });
  }, []);

  return { theme, toggleTheme };
};

export default useTheme;
